import React, { useState, useMemo, useEffect, useRef } from "react";
import { Col, Container, Row, Card, Form, Button, ButtonGroup, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "./Controls/PageTitle";
import { DashboardPieChart, DashboardLineChart, DashboardBarChart } from "./Controls/Dashboard";
import { Calendar, GraphUp, CurrencyDollar } from "react-bootstrap-icons";
import { useAtom } from "jotai";
import { maskAtom } from "./Data/Atoms";
import { CSSTransition } from "react-transition-group";
import { createApiService } from "./shared/apiConfig";
import { FacilityApi, ServerTransferDashboardStats, ServerTransferFacilityDashboardStats, ServerTransferDashboardMonthlyStat } from "./shared/api/axios-client/api";
import { showMoney, cents } from "./Data/Money";
import { Button as SsButton } from "./Controls/Button";
import { navigationService } from "./services/NavigationService";

// Simple animated counter hook using CSS transitions
const useSimpleAnimatedCounter = (endValue: number, duration: number = 800) => {
    const [displayValue, setDisplayValue] = useState(endValue);
    const [isAnimating, setIsAnimating] = useState(false);
    const prevValueRef = useRef(endValue);

    useEffect(() => {
        if (prevValueRef.current !== endValue) {
            setIsAnimating(true);
            
            // Start animation immediately
            const startValue = displayValue;
            const difference = endValue - startValue;
            const startTime = Date.now();
            
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Simple easing
                const easeProgress = 1 - Math.pow(1 - progress, 2);
                const currentValue = startValue + (difference * easeProgress);
                
                setDisplayValue(currentValue);
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    setDisplayValue(endValue);
                    setIsAnimating(false);
                }
            };
            
            requestAnimationFrame(animate);
            prevValueRef.current = endValue;
        }
    }, [endValue, duration, displayValue]);

    return { displayValue: Math.round(displayValue), isAnimating };
};

// Animated MetricTile component
interface AnimatedMetricTileProps {
    title: string;
    value: number;
    icon: React.ReactNode;
    color?: string;
    duration?: number;
}

function AnimatedMetricTile({ title, value, icon, color = "primary", duration = 800 }: AnimatedMetricTileProps) {
    const { displayValue, isAnimating } = useSimpleAnimatedCounter(value, duration);
    
    return (
        <Card className="h-100 shadow-sm border-0" style={{
            animation: 'fadeInUp 0.6s ease-out forwards',
            opacity: 0,
            transform: 'translateY(20px)'
        }}>
            <Card.Body className="d-flex align-items-center">
                                <div className={`bg-success bg-opacity-10 rounded-3 p-3 me-3`} style={{
                    animation: 'scaleIn 0.4s ease-out 0.3s forwards',
                    transform: 'scale(0)'
                }}>
                    <div className={`text-success fs-4`}>
                        {icon}
                    </div>
                </div>
                <div>
                    <div className="text-muted small">{title}</div>
                    <div 
                        className={`fs-3 fw-bold text-${color}`}
                        style={{
                            animation: 'slideInRight 0.5s ease-out 0.4s forwards',
                            opacity: 0,
                            transform: 'translateX(-10px)',
                            transition: 'transform 0.2s ease, color 0.3s ease',
                            filter: isAnimating ? 'brightness(1.2)' : 'brightness(1)'
                        }}
                    >
                        {formatMoneyForChart(displayValue)}
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
}

// Custom color scheme generator - creates lighter tints of a base color
const generateCustomColorScheme = (baseColor: string, numColors: number): string[] => {
    // Convert hex to RGB
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const colors: string[] = [baseColor]; // Start with the darkest color
    
    // Generate lighter tints by mixing with white
    for (let i = 1; i < numColors; i++) {
        const ratio = i / (numColors - 1); // 0 to 1
        const lightness = Math.pow(ratio, 0.6); // Use power curve for better distribution
        
        const newR = Math.round(r + (255 - r) * lightness);
        const newG = Math.round(g + (255 - g) * lightness);
        const newB = Math.round(b + (255 - b) * lightness);
        
        const newHex = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        colors.push(newHex);
    }
    
    return colors;
};

// Create custom color scheme with #D61F84 as darkest and progressively lighter variations
const customPinkScheme = generateCustomColorScheme('#D61F84', 16);

// Custom formatting function for chart labels
const formatMoneyForChart = (centsValue: number): string => {
    const money = cents(centsValue);
    const dollarValue = centsValue / 100; // Convert cents to dollars
    
    if (dollarValue >= 1000) {
        const kValue = dollarValue / 1000;
        return `$${kValue.toFixed(1)}K`;
    } else {
        return showMoney(money);
    }
};

// Helper function to check if all values in chart data are 0
const hasNoData = (data: Array<{ value: number }>): boolean => {
    return data.every(item => item.value === 0);
};

// Helper function to limit pie chart data and group smaller items as "Others"
const limitPieChartData = (data: Array<{id: string, label: string, value: number}>, maxItems: number = 15) => {
    if (data.length <= maxItems) {
        return data;
    }
    
    const topItems = data.slice(0, maxItems - 1);
    const remainingItems = data.slice(maxItems - 1);
    const othersValue = remainingItems.reduce((sum, item) => sum + item.value, 0);
    
    if (othersValue > 0) {
        topItems.push({
            id: 'Others',
            label: `Others (${remainingItems.length})`,
            value: othersValue
        });
    }
    
    return topItems;
};

// Line chart Y-axis formatter - formats dollar values with commas and K format (matches pie charts)
const formatLineChartYAxis = (dollarValue: number): string => {
    if (dollarValue >= 1000) {
        const kValue = dollarValue / 1000;
        return `$${kValue.toFixed(1)}K`;
    } else {
        // Convert dollars back to cents to use showMoney for proper comma formatting
        const centsValue = Math.round(dollarValue * 100);
        return showMoney(cents(centsValue));
    }
};

// Updated interfaces to match the actual API response structure
interface FacilityData {
    id: number;
    name: string;
    revenue: number;
    openBalance: number;
    transientRevenue: number;
    subscriptionParkerCount: number;
    transientCount: number;
}

interface MonthlyData {
    month: string;
    revenue: number;
    transient: number;
}

interface DashboardApiResponse {
    facilities: FacilityData[];
    monthlyData: MonthlyData[];
    totalRevenue: number;
    totalOpenBalance: number;
    totalFacilities: number;
    // Static line chart data - only returned on initial load
    staticLineChartData?: {
        monthlyRevenueLineData: any[];
        transientLineData: any[];
    };
}

// Function to transform API response to our expected format
const transformApiResponse = (apiData: ServerTransferDashboardStats[] | ServerTransferDashboardStats, isInitialLoad: boolean = false): DashboardApiResponse => {
    // Handle both array and single object responses
    const dashboardStats = Array.isArray(apiData) ? apiData[0] : apiData;
    
    // Safety check for dashboardStats
    if (!dashboardStats) {
        return {
            facilities: [],
            monthlyData: [],
            totalRevenue: 0,
            totalOpenBalance: 0,
            totalFacilities: 0
        };
    }
    
    // Transform facility data
    const facilities: FacilityData[] = (dashboardStats.facilityDashboardStats || []).map(item => ({
        id: item.facilityId || 0,
        name: item.facilityName || "Unknown Facility",
        revenue: (item.subscriptionRevenue || 0) + (item.transientRevenue || 0),
        openBalance: item.totalUnpaidBalance || 0,
        transientRevenue: item.transientRevenue || 0,
        subscriptionParkerCount: item.subscriptionParkerCount || 0,
        transientCount: item.transientCount || 0
    }));

    const totalRevenue = facilities.reduce((sum, facility) => sum + facility.revenue, 0);
    const totalOpenBalance = facilities.reduce((sum, facility) => sum + facility.openBalance, 0);
    const totalFacilities = facilities.length;

    // Only process monthly trend data on initial load (for static line charts)
    let monthlyData: MonthlyData[] = [];
    let staticLineChartData: {
        monthlyRevenueLineData: any[];
        transientLineData: any[];
    } | undefined = undefined;
    
    if (isInitialLoad) {
        // Create a map to combine subscription and transient revenue by month
        const monthlyRevenueMap = new Map<string, { subscription: number, transient: number }>();
        
        // Process subscription revenue trend
        (dashboardStats.subscriptionRevTrend || []).forEach(item => {
            if (item.yearMonth && item.revenue !== null && item.revenue !== undefined) {
                const existing = monthlyRevenueMap.get(item.yearMonth) || { subscription: 0, transient: 0 };
                existing.subscription = item.revenue;
                monthlyRevenueMap.set(item.yearMonth, existing);
            }
        });
        
        // Process transient revenue trend
        (dashboardStats.transientRevTrend || []).forEach(item => {
            if (item.yearMonth && item.revenue !== null && item.revenue !== undefined) {
                const existing = monthlyRevenueMap.get(item.yearMonth) || { subscription: 0, transient: 0 };
                existing.transient = item.revenue;
                monthlyRevenueMap.set(item.yearMonth, existing);
            }
        });
        
        // Convert to MonthlyData format
        Array.from(monthlyRevenueMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([yearMonth, revenues]) => {
                // Convert "03/2025" to "Mar"
                const [month, year] = yearMonth.split('/');
                const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                const monthName = date.toLocaleDateString('en-US', { month: 'short' });
                
                monthlyData.push({
                    month: monthName,
                    revenue: revenues.subscription + revenues.transient,
                    transient: revenues.transient
                });
            });

        // Prepare static line chart data - convert cents to dollars
        staticLineChartData = {
            monthlyRevenueLineData: [{
                id: 'Monthly Revenue',
                data: monthlyData.map(m => ({ x: m.month, y: m.revenue / 100 }))
            }],
            transientLineData: [{
                id: 'Transient Revenue',
                data: monthlyData.map(m => ({ x: m.month, y: m.transient / 100 }))
            }]
        };
    }

    return {
        facilities,
        monthlyData,
        totalRevenue,
        totalOpenBalance,
        totalFacilities,
        staticLineChartData
    };
};

// API function using the actual API client
const getFacilitiesDashboard = async (days: number, isInitialLoad: boolean = false): Promise<DashboardApiResponse> => {
    const facilityService = createApiService(FacilityApi);
    const response = await facilityService.getFacilitiesDashboard({ daysBack: days, isInitialLoad: isInitialLoad });
    return transformApiResponse(response.data, isInitialLoad);
};

// Note: Old stubbed data generation functions removed - now using API data

// Date filter options
const dateFilterOptions = [
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
    { label: 'Last 365 Days', value: '365d' },
    { label: 'This Month', value: 'month' },
    { label: 'This Quarter', value: 'quarter' }
];

interface MetricTileProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color?: string;
}

function MetricTile({ title, value, icon, color = "primary" }: MetricTileProps) {
    return (
        <Card className="h-100 shadow-sm border-0" style={{
            animation: 'fadeInUp 0.6s ease-out forwards',
            opacity: 0,
            transform: 'translateY(20px)'
        }}>
            <Card.Body className="d-flex align-items-center">
                <div className={`bg-${color} bg-opacity-10 rounded-3 p-3 me-3`} style={{
                    animation: 'scaleIn 0.4s ease-out 0.3s forwards',
                    transform: 'scale(0)'
                }}>
                    <div className={`text-${color} fs-4`}>
                        {icon}
                    </div>
                </div>
                <div>
                    <div className="text-muted small">{title}</div>
                    <div className="fs-3 fw-bold" style={{
                        animation: 'slideInRight 0.5s ease-out 0.4s forwards',
                        opacity: 0,
                        transform: 'translateX(-10px)'
                    }}>{value}</div>
                </div>
            </Card.Body>
        </Card>
    );
}

interface ChartTileProps {
    title: string;
    height?: number;
    children: React.ReactNode;
}

function ChartTile({ title, height = 300, children }: ChartTileProps) {
    return (
        <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-white border-0 pb-0">
                <h6 className="mb-0 fw-bold text-muted">{title}</h6>
            </Card.Header>
            <Card.Body>
                <div style={{ height: `${height}px` }}>
                    {children}
                </div>
            </Card.Body>
        </Card>
    );
}

export function OperatorDashboard() {
    const [dateFilter, setDateFilter] = useState('7d');
    const [, setMask] = useAtom(maskAtom);
    const [showContent, setShowContent] = useState(false);
    const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);
    const [staticLineChartData, setStaticLineChartData] = useState<{
        monthlyRevenueLineData: any[];
        transientLineData: any[];
    } | null>(null);
    const [selectedFacility, setSelectedFacility] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const nodeRef = useRef(null);
    const hasLoadedRef = useRef(false);
    const fetchInProgressRef = useRef(false);
    const navigate = useNavigate();

    // Convert date filter to days
    const getDaysFromFilter = (filter: string): number => {
        const today = new Date();
        
        switch (filter) {
            case '7d': return 7;
            case '30d': return 30;
            case '90d': return 90;
            case '365d': return 365;
            case 'month': {
                // Calculate days from today back to first day of current month
                const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const diffTime = today.getTime() - firstOfMonth.getTime();
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include today
            }
            case 'quarter': {
                // Calculate days from today back to first day of current quarter
                const currentMonth = today.getMonth(); // 0-11
                const quarterStartMonth = Math.floor(currentMonth / 3) * 3; // 0, 3, 6, or 9
                const firstOfQuarter = new Date(today.getFullYear(), quarterStartMonth, 1);
                const diffTime = today.getTime() - firstOfQuarter.getTime();
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include today
            }
            default: return 7;
        }
    };

    // Fetch dashboard data
    const fetchDashboardData = async (days: number, isInitialLoad: boolean = false) => {
        // Prevent concurrent API calls
        if (fetchInProgressRef.current) {
            return;
        }

        try {
            fetchInProgressRef.current = true;
            
            // global mask via interceptors

            const data = await getFacilitiesDashboard(days, isInitialLoad);
            setDashboardData(data);
            
            // Store static line chart data on initial load
            if (isInitialLoad && data.staticLineChartData) {
                setStaticLineChartData(data.staticLineChartData);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            // You might want to add error state handling here
        } finally {
            fetchInProgressRef.current = false;
            // Only trigger page animations on initial load, not on filter changes
            if (isInitialLoad) {
                setShowContent(true);
            }
        }
    };

    // Initial data load and filter changes
    useEffect(() => {
        const days = getDaysFromFilter(dateFilter);
        
        // Check if this is the initial load or a filter change
        const isInitialLoad = !hasLoadedRef.current;
        
        fetchDashboardData(days, isInitialLoad);
        
        // Mark as loaded after the first successful call
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
        }
    }, [dateFilter]);

    // Handle date filter changes
    const handleDateFilterChange = (newFilter: string) => {
        setDateFilter(newFilter);
        // fetchDashboardData will be called by useEffect
        // Chart animations will trigger naturally when data changes
    };

    // Handle pie chart slice click
    const handleSliceClick = (data: { id: string; label: string; value: number; facilityId?: number }) => {
        if (data.facilityId && data.facilityId !== undefined) {
            setSelectedFacility({
                id: data.facilityId,
                name: data.label
            });
        }
    };

    // Handle facility navigation
    const handleNavigateToFacility = () => {
        if (selectedFacility) {
            navigationService.navigateToFacilityFromDashboard(navigate, selectedFacility.id);
        }
    };

    // Close facility modal
    const handleCloseFacilityModal = () => {
        setSelectedFacility(null);
    };

    // Don't render anything if data isn't loaded yet
    if (!showContent || !dashboardData) {
        return null;
    }

    const { facilities: facilityData, monthlyData, totalRevenue, totalOpenBalance, totalFacilities } = dashboardData;
    
    // Prepare chart data for bar charts
    const revenueBarData = facilityData.map(f => ({
        facility: f.name.replace(' ', '\n'), // Short names for better display
        revenue: f.revenue // Keep as cents for proper chart scaling
    }));
    
    const monthlyRevenueBarData = monthlyData.slice(0, 6).map(m => ({
        month: m.month,
        revenue: m.revenue // Keep as cents for proper chart scaling
    }));

    // Prepare chart data for pie charts
    const revenuePieData = facilityData.map(f => ({
        id: f.name,
        label: f.name,
        value: f.revenue,
        facilityId: f.id
    }));
    
    const openBalancePieData = facilityData.map(f => ({
        id: f.name,
        label: f.name,
        value: f.openBalance,
        facilityId: f.id
    }));
    
    const transientPieData = facilityData.map(f => ({
        id: f.name,
        label: f.name,
        value: f.transientRevenue,
        facilityId: f.id
    }));

    const spacesPieData = facilityData.map(f => ({
        id: f.name,
        label: f.name,
        value: f.subscriptionParkerCount, // Assuming subscriptionParkerCount is the number of spaces
        facilityId: f.id
    }));
    
    const transientCountPieData = facilityData.map(f => ({
        id: f.name,
        label: f.name,
        value: f.transientCount,
        facilityId: f.id
    }));

    // Prepare chart data for line charts - use static data if available, otherwise use current data
    const monthlyRevenueLineData = staticLineChartData?.monthlyRevenueLineData || [{
        id: 'Monthly Revenue',
        data: monthlyData.map(m => ({ x: m.month, y: m.revenue / 100 }))
    }];
    
    const transientLineData = staticLineChartData?.transientLineData || [{
        id: 'Transient Revenue',
        data: monthlyData.map(m => ({ x: m.month, y: m.transient / 100 }))
    }];

    return (
        <>
            <CSSTransition
                in={showContent}
                timeout={300}
                classNames="page-slide"
                unmountOnExit
                nodeRef={nodeRef}
            >
                <Container fluid className="p-4" ref={nodeRef}>
            <style>
                {`
                    @keyframes fadeInUp {
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes scaleIn {
                        to {
                            transform: scale(1);
                        }
                    }
                    
                    @keyframes slideInRight {
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes chartFadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    .chart-tile {
                        animation: chartFadeIn 0.8s ease-out forwards;
                        opacity: 0;
                    }
                    
                    .chart-tile:nth-child(1) { animation-delay: 0.1s; }
                    .chart-tile:nth-child(2) { animation-delay: 0.2s; }
                    .chart-tile:nth-child(3) { animation-delay: 0.3s; }
                    
                    .metric-tile:nth-child(1) { animation-delay: 0.1s; }
                    .metric-tile:nth-child(2) { animation-delay: 0.2s; }
                    .metric-tile:nth-child(3) { animation-delay: 0.3s; }
                `}
            </style>
            <Row className="mb-4">
                <Col>
                    <PageTitle className="mb-3">
                        <GraphUp className="me-2" />
                        Operator Dashboard
                    </PageTitle>
                </Col>
            </Row>

            {/* Date Filter Controls */}
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="py-3">
                            <div className="d-flex align-items-center gap-3">
                                <Calendar className="text-muted" />
                                <span className="fw-semibold text-muted">Filter:</span>
                                <ButtonGroup size="sm">
                                    {dateFilterOptions.map(option => (
                                        <Button
                                            key={option.value}
                                            variant={dateFilter === option.value ? "primary" : "outline-primary"}
                                            onClick={() => handleDateFilterChange(option.value)}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                                <small className="text-muted ms-auto">
                                    <b>Note:</b> Charts not affected by filters are marked with an asterisk (*)
                                </small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Metric Tiles Row */}
            <Row className="mb-4 g-3">
                <Col lg={4} className="metric-tile">
                    <MetricTile
                        title="Total Facilities"
                        value={totalFacilities}
                        icon={<GraphUp className="icon-shift-up" />}
                        color="dark"
                    />
                </Col>
                <Col lg={4} className="metric-tile">
                    <AnimatedMetricTile
                        title="Total Revenue"
                        value={totalRevenue}
                        icon={<CurrencyDollar className="icon-shift-up" />}
                        color="dark"
                        duration={600}
                    />
                </Col>
                <Col lg={4} className="metric-tile">
                    <MetricTile
                        title="Current Open Balance*"
                        value={formatMoneyForChart(totalOpenBalance)}
                        icon={<CurrencyDollar className="icon-shift-up" />}
                        color="danger"
                    />
                </Col>
            </Row>

            {/* Chart Tiles Row 1: Pie Charts */}
            <Row className="mb-4 g-3">
                {/* Revenue by Facility Pie Chart */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Revenue by Facility">
                        <DashboardPieChart
                            data={revenuePieData}
                            legendConfig="right"
                            startAngle={0}
                            endAngle={360}
                            transitionMode="startAngle"
                            innerRadius={0.4}  // Smaller inner radius for more space
                            colors={customPinkScheme}
                            valueFormat={(value) => formatMoneyForChart(value)}
                            showNoDataMessage={hasNoData(revenuePieData)}
                            onSliceClick={handleSliceClick}
                        />
                    </ChartTile>
                </Col>

                {/* Open Balance by Facility Pie Chart */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Open Balance by Facility*">
                        <DashboardPieChart
                            data={openBalancePieData}
                            legendConfig="right"
                            startAngle={90}
                            endAngle={450}
                            transitionMode="endAngle"
                            innerRadius={0.4}  // Smaller inner radius for more space
                            colors={customPinkScheme}
                            valueFormat={(value) => formatMoneyForChart(value)}
                            showNoDataMessage={hasNoData(openBalancePieData)}
                            onSliceClick={handleSliceClick}
                        />
                    </ChartTile>
                </Col>


            </Row>

            {/* Chart Tiles Row 2: Line Charts */}
            <Row className="mb-4 g-3">
                {/* Monthly Revenue Line Chart */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Monthly Revenue Trend*" height={350}>
                        <DashboardLineChart
                            data={monthlyRevenueLineData}
                            xAxisLabel="Month"
                            yAxisLabel="Revenue ($K)"
                            height={350}
                            yAxisFormat={formatLineChartYAxis}
                            yValueFormat={formatLineChartYAxis}
                            enableLegend={false}
                        />
                    </ChartTile>
                </Col>

                {/* Monthly Revenue by Facility Pie Chart */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Monthly Revenue by Facility">
                        <DashboardPieChart
                            data={revenuePieData}
                            legendConfig="right"
                            startAngle={180}
                            endAngle={540}
                            transitionMode="startAngle"
                            innerRadius={0.4}  // Smaller inner radius for more space
                            colors={customPinkScheme}
                            valueFormat={(value) => formatMoneyForChart(value)}
                            showNoDataMessage={hasNoData(revenuePieData)}
                            onSliceClick={handleSliceClick}
                        />
                    </ChartTile>
                </Col>
            </Row>

            {/* Chart Tiles Row 3: More Line and Pie Charts */}
            <Row className="mb-4 g-3">
                {/* Transient Revenue Over Time */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Transient Revenue Trend*" height={350}>
                        <DashboardLineChart
                            data={transientLineData}
                            xAxisLabel="Month"
                            yAxisLabel="Transient Revenue ($K)"
                            height={350}
                            yAxisFormat={formatLineChartYAxis}
                            yValueFormat={formatLineChartYAxis}
                            enableLegend={false}
                        />
                    </ChartTile>
                </Col>

                {/* Transient Revenue by Facility Pie Chart */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Transient Revenue by Facility">
                        <DashboardPieChart
                            data={transientPieData}
                            legendConfig="right"
                            startAngle={270}
                            endAngle={630}
                            transitionMode="endAngle"
                            innerRadius={0.4}  // Smaller inner radius for more space
                            colors={customPinkScheme}
                            valueFormat={(value) => formatMoneyForChart(value)}
                            showNoDataMessage={hasNoData(transientPieData)}
                            onSliceClick={handleSliceClick}
                        />
                    </ChartTile>
                </Col>
            </Row>

            {/* Chart Tiles Row 4: Final Charts */}
            <Row className="mb-4 g-3">
                {/* Subscription Parker Count by Facility */}
                <Col lg={6} className="chart-tile">
                    <ChartTile title="Subscription Parker Count by Facility">
                        <DashboardPieChart
                            data={spacesPieData}
                            legendConfig="right"
                            startAngle={0}
                            endAngle={360}
                            transitionMode="startAngle"
                            innerRadius={0.4}  // Smaller inner radius for more space
                            colors={customPinkScheme}
                            showNoDataMessage={hasNoData(spacesPieData)}
                            onSliceClick={handleSliceClick}
                        />
                    </ChartTile>
                </Col>

                {/* Transient Count */}
                <Col lg={6} className="chart-tile">
                                 <ChartTile title="Transient Count by Facility">
                         <DashboardPieChart
                             data={transientCountPieData}
                             legendConfig="right"
                             startAngle={0}
                             endAngle={360}
                             transitionMode="startAngle"
                             innerRadius={0.4}  // Smaller inner radius for more space
                             colors={customPinkScheme}
                             showNoDataMessage={hasNoData(transientCountPieData)}
                             onSliceClick={handleSliceClick}
                         />
                     </ChartTile>
                </Col>
            </Row>
                 
            </Container>
        </CSSTransition>
        
        {/* Facility Navigation Modal */}
        <Modal show={selectedFacility !== null} onHide={handleCloseFacilityModal} centered>
         
            <Modal.Body className="text-center">
                <p>Get the details for <strong>{selectedFacility?.name}</strong>?</p>
                <div className="text-center mb-2">
                    <SsButton onClick={handleCloseFacilityModal}>
                        Cancel
                    </SsButton>
                    <SsButton className="btn-main mx-2" onClick={handleNavigateToFacility}>
                        Go to Facility Overview
                    </SsButton>
                </div>
            </Modal.Body>
            
        </Modal>
        </>
    );
}